import DynamicForm from "../DynamicForm/DynamicForm";
import {
  Box,
  Button,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  IAsyncLabelFn,
  IAsyncOptionFn,
  IDynamicFormFieldsJson,
  IGetOptionsResponse,
} from "../../models/user/DynamicForm/DynamicForm";
import {
  API_GET_LOOKUP_VALUES,
  API_POST_INSURED_ADDRESS_DETAILS,
} from "../../Apis/ApiEndPoints";
import { CustomAction } from "../../store/customAction";
import { RootState } from "../../store/store";
import { useParams } from "react-router-dom";
import { PostAddVehicle } from "../../store/user/policyChange/policyChangeActions";
import { IVehiclePolicyChangeRequestParams } from "../../models/user/PolicyChange/PolicyChange";
import {
  GetAddVehicleFormFields,
  GetVehicleDetails,
} from "../../store/user/vehicle/vehicleActions";
import { setVehicleDetails } from "../../store/user/vehicle/vehicleSlice";
import {
  IGetInsuredAddressDetailsResponse,
  IGetVehcileDetailsRequestParams,
} from "../../models/user/Vehicle/Vehicle";
import MessageDialog from "../../components/MessageDialog/MessageDialog";
import { Api } from "../..";
import { useTranslation } from "react-i18next";
import { getCookie } from "../../utils/Utility";
import { setCalledReducerType } from "../../store/common/CommonSlice";

export interface IProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  changePolicyId?: string;
  changePolicyLobCode?: string;
  onErrorClose?: () => void;
}

const AddVehicleForm: React.FC<IProps> = ({
  open,
  setOpen,
  changePolicyId,
  changePolicyLobCode,
  onErrorClose,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  let { policyId, lobCode } = useParams();

  policyId = policyId ? policyId : changePolicyId;
  lobCode = lobCode ? lobCode : changePolicyLobCode;

  const carrier = getCookie("carrier");
  const system = getCookie("system");

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { policyDetailsHeader } = useAppSelector(
    (state) => state.policyDetails
  );
  const { addVehicleFormFieldsJson, vehicleDetails } = useAppSelector(
    (state: RootState) => state.vehicle
  );
  const { isSuccess, isError, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );

  const [vin, setVin] = useState<string>("");
  const [vinError, setVinError] = useState<string>("");
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const asyncOptionsFunctions: {
    [key: string]: IAsyncOptionFn;
  } = useMemo(
    () => ({
      getVehicleOwnershipOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getVehicleOwnershipOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getVehicleUsageOptions: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getVehicleUsageOptions"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
      getInterestTypes: async (fieldName) => {
        try {
          const response: IGetOptionsResponse = await CustomAction(
            Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
            dispatch,
            "getInterestTypes"
          );
          return response.data;
        } catch (error) {
          return [];
        }
      },
    }),
    [dispatch]
  );

  const asyncLabelFunctions: { [key: string]: IAsyncLabelFn } = useMemo(
    () => ({
      getGarageLocation: async () => {
        try {
          const response: IGetInsuredAddressDetailsResponse =
            await CustomAction(
              Api.post(API_POST_INSURED_ADDRESS_DETAILS, {
                policyId: policyId ? policyId : changePolicyId,
                carrier,
                system,
                lobCode,
              }),
              dispatch,
              "getGarageLocation"
            );
          return `${response.data.address.addr1} ${response.data.address.city} ${response.data.address.county} ${response.data.address.stateProvCd} ${response.data.address.regionCd} ${response.data.address.postalCode}`;
        } catch (error) {
          return "";
        }
      },
    }),
    [dispatch, policyId, carrier, system, lobCode, changePolicyId]
  );

  const handleVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVinError("");
    setVin(e.target.value);
  };

  const handleLookUp = () => {
    if (vin.length !== 17) {
      setVinError(t("Please enter a valid vin number"));
      return;
    }
    if (policyId && carrier && lobCode && system) {
      const requestParams: IGetVehcileDetailsRequestParams = {
        vin,
        carrier,
        lobCode,
        system,
      };
      dispatch(GetVehicleDetails(requestParams));
    }
  };

  const formFieldsJson: IDynamicFormFieldsJson | null =
    useMemo((): IDynamicFormFieldsJson | null => {
      if (vehicleDetails && addVehicleFormFieldsJson) {
        const vehicleDetailsKeys = Object.keys(vehicleDetails);
        return {
          ...addVehicleFormFieldsJson,
          sections: addVehicleFormFieldsJson.sections
            ? addVehicleFormFieldsJson.sections.map((section) => ({
                ...section,
                fields: section.fields.map((field) => {
                  const updatedField = { ...field };
                  if (vehicleDetailsKeys.includes(updatedField.name)) {
                    updatedField.value =
                      vehicleDetails[
                        updatedField.name as keyof typeof vehicleDetails
                      ] ?? undefined;
                  }
                  if (updatedField.name === "carrierPolicyNo") {
                    updatedField.value =
                      policyDetailsHeader?.carrierPolicyNumber;
                  }
                  return updatedField;
                }),
              }))
            : [],
        };
      }
      return null;
    }, [vehicleDetails, addVehicleFormFieldsJson, policyDetailsHeader]);

  const handleAddVehicleFormSubmit = (formValues: { [key: string]: any }) => {
    if (vin.length !== 17) {
      setVinError(t("Invalid Vin Number"));
      return;
    }
    if (
      userDetails &&
      userDetails.customerId &&
      policyId &&
      carrier &&
      system &&
      lobCode
    ) {
      const addVehicleReuestParams: IVehiclePolicyChangeRequestParams = {
        carrierPolicyNo: formValues.carrierPolicyNo,
        changeDt: formValues.changeDt.format("YYYY-MM-DD"),
        customerId: userDetails.customerId,
        policyRef: policyId,
        carrier,
        system,
        detailDescription: "Adding a vehicle to the  policy from UI",
        lobCode,
        vehicle: {
          make: formValues.make,
          manufactureYr: formValues.manufactureYr,
          model: formValues.model,
          vehicleType: formValues.vehicleType,
          vin: vin,
          address: null,
          vehicleUsage: formValues.vehicleUsage,
          annualMileage: formValues.annualMileage,
        },
        additionalInterestDetails: {
          loanNumber: formValues.loanNumber,
          // address: formValues.additionalInterestAddress,
          address: null,
          interestName: formValues.interestName,
          interestTypeCd: formValues.interestType || "Leinholder/Loss Payee",
          status: "",
          phoneInfo: "",
        },
      };
      dispatch(PostAddVehicle(addVehicleReuestParams));
    }
  };

  const handleClose = useCallback(() => {
    dispatch(setVehicleDetails(null));
    setVin("");
    setOpen(false);
  }, [dispatch, setOpen]);

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
    setCalledReducerType(null);
    if (
      isError &&
      ((calledReducerType === "vehicle/GetVehicleDetails" ||
        calledReducerType === "vehicle/GetInsuredAddressDetails") ||
        calledReducerType === "policyChange/PostAddVehicle") &&
      typeof onErrorClose === "function"
    ) {
      onErrorClose();
    } else {
      handleClose();
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(GetAddVehicleFormFields());
      dispatch(setCalledReducerType(null));
      dispatch(setVehicleDetails(null));
      setVinError("");
      setVin("");
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (
      (isError &&
        (calledReducerType === "vehicle/GetVehicleDetails" ||
          calledReducerType === "vehicle/GetInsuredAddressDetails")) ||
      ((isSuccess || isError) &&
        calledReducerType === "policyChange/PostAddVehicle")
    ) {
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType, handleClose, setOpen]);

  return (
    <>
      {/* <Drawer
        anchor="right"
        PaperProps={{
          sx: {
            width: "50%",
            "@media (max-width:768px)": {
              width: "100%",
            },
          },
        }}
        open={open}
        onClose={handleClose}
      >
        <Box sx={AddVehcileStyles.heading}>
          <Typography sx={AddVehcileStyles.headingText}>
            {t("Add Vehicle")}
          </Typography>
          <CloseOutlined sx={{ cursor: "pointer" }} onClick={handleClose} />
        </Box> */}
      <Box
        sx={{
          padding: "20px 20px 0px 20px",
          display: "grid",
          gridTemplateColumns: "80% 20%",
          gap: "12px",
          alignItems: "center",
        }}
      >
        <Box>
          <TextField
            id="VIN-text-area"
            data-test-id="test-VIN-text-area"
            aria-label="VIN text area"
            label={t("VIN")}
            value={vin}
            fullWidth
            placeholder="Please enter the VIN of vehicle to be added"
            onChange={handleVinChange}
            required
          />
          <Typography sx={{ color: "red" }}>{vinError}</Typography>
        </Box>
        <Button
          id="Look-up-btn"
          data-test-id="test-Look-up"
          aria-label="Look up"
          variant="contained"
          onClick={handleLookUp}
        >
          {t("Look up")}
        </Button>
      </Box>
      <DialogContent>
        {formFieldsJson ? (
          <DynamicForm
            formFieldsJson={formFieldsJson}
            onFormSubmit={handleAddVehicleFormSubmit}
            asyncOptionsFunctions={asyncOptionsFunctions}
            asyncLabelFunctions={asyncLabelFunctions}
          />
        ) : (
          <></>
        )}
      </DialogContent>
      {/* </Drawer> */}
      <MessageDialog
        handleClose={handleMessageDialogClose}
        open={messageDialogOpen}
      />
    </>
  );
};

export default AddVehicleForm;
